import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useTelemetry, useTrackEffect } from '@order/Telemetry';

import { useIsLoggedIn } from '../../context/AuthMachine';
import { useOrderFeedbackNotifications } from '../../hooks';

export const useRateOrder = () => {
  const { formatMessage } = useIntl();
  const isLoggedIn = useIsLoggedIn();

  useTrackEffect('rate_order.view', {});

  const { cancelScheduledOrderFeedbackNotification } = useOrderFeedbackNotifications();

  const { track } = useTelemetry();

  const handleSubmit: HandleRatingSubmission = useCallback(
    async (orderId: string, isSuccessful: boolean) => {
      if (!isLoggedIn) return;

      const userError = formatMessage(messages.rateOrderResponseFailure);

      track(
        isSuccessful ? 'rate_order.success' : 'rate_order.failure',
        isSuccessful ? {} : { userError, systemError: undefined },
      );

      if (isSuccessful) {
        await cancelScheduledOrderFeedbackNotification(orderId);
      }
    },
    [isLoggedIn, formatMessage, track, cancelScheduledOrderFeedbackNotification],
  );

  return {
    handleSubmit,
  };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  rateOrderResponseFailure: {
    defaultMessage: 'We were unable to submit your feedback. Please try again.',
    description: 'Rate Order | Response | Failure',
  },
  rateOrderResponseSuccess: {
    defaultMessage: `Thank you! Your feedback means a lot. We're always hungry to improve your experience.`,
    description: 'Rate Order | Response | Success',
  },
  rateOrderHint: {
    defaultMessage: 'Choose all that apply',
    description: 'Rate Order | Hint',
  },
  rateOrderCallToAction: {
    defaultMessage: 'Submit',
    description: 'Rate Order | Call To Action',
  },
  rateOrderError: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'Rate Order | Error',
  },
  subtitleFull: {
    defaultMessage:
      '{products} from {restaurant} on {wanted_time, date, medium} at {wanted_time, time, short}',
    description: 'Rate Order | Subtitle | Full',
  },
  subtitleNoProducts: {
    defaultMessage:
      'From {restaurant} on {wanted_time, date, medium} at {wanted_time, time, short}',
    description: 'Rate Order | Subtitle | No Products',
  },
  subtitleNoRestaurant: {
    defaultMessage: '{products} on {wanted_time, date, medium} at {wanted_time, time, short}',
    description: 'Rate Order | Subtitle | No Restaurant',
  },
  subtitleNoTime: {
    defaultMessage: '{products} from {restaurant}',
    description: 'Rate Order | Subtitle | No Time',
  },
  subtitleOnlyProducts: {
    defaultMessage: '{products}',
    description: 'Rate Order | Subtitle | Only Products',
  },
  subtitleOnlyRestaurant: {
    defaultMessage: 'From {restaurant}',
    description: 'Rate Order | Subtitle | Only Restaurant',
  },
  subtitleOnlyTime: {
    defaultMessage: 'On {wanted_time, date, medium} at {wanted_time, time, short}',
    description: 'Rate Order | Subtitle | Only Time',
  },
  rateOrderStarsTitle1: {
    defaultMessage: 'Awful',
    description: 'Rate Order | Stars Title | 1',
  },
  rateOrderStarsTitle2: {
    defaultMessage: 'Bad',
    description: 'Rate Order | Stars Title | 2',
  },
  rateOrderStarsTitle3: {
    defaultMessage: 'Disappointing',
    description: 'Rate Order | Stars Title | 3',
  },
  rateOrderStarsTitle4: {
    defaultMessage: 'Pretty good, but...',
    description: 'Rate Order | Stars Title | 4',
  },
  rateOrderStarsTitle5: {
    defaultMessage: 'Perfection',
    description: 'Rate Order | Stars Title | 5',
  },
});

export type HandleRatingSubmission = (orderId: string, isSuccessful: boolean) => Promise<void>;
